// frontend/src/components/integrations/hooks/useCanonicalProperties.js
import { useState, useCallback } from 'react';
import apiClient from '../../../utils/axios';

export default function useCanonicalProperties() {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/integrations/canonical-properties');
      setDefinitions(res.data.definitions || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, []);

  const createProperty = useCallback(async (payload) => {
    const res = await apiClient.post('/integrations/canonical-properties', payload);
    setDefinitions(prev => [...prev, res.data]);
    return res.data;
  }, []);

  const updateProperty = useCallback(async (key, updates) => {
    await apiClient.patch(`/integrations/canonical-properties/${key}`, updates);
    setDefinitions(prev => prev.map(d => d.key === key ? { ...d, ...updates } : d));
  }, []);

  const deleteProperty = useCallback(async (key) => {
    await apiClient.delete(`/integrations/canonical-properties/${key}`);
    setDefinitions(prev => prev.filter(d => d.key !== key));
  }, []);

  const upsertLink = useCallback(async (key, integrationId, linkPayload) => {
    await apiClient.put(
      `/integrations/canonical-properties/${key}/links/${integrationId}`,
      linkPayload,
    );
    await load();
  }, [load]);

  const removeLink = useCallback(async (key, integrationId) => {
    await apiClient.delete(
      `/integrations/canonical-properties/${key}/links/${integrationId}`,
    );
    await load();
  }, [load]);

  const fetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      // With ai.suggestions.enabled, this makes one real LLM call per integration,
      // which can comfortably exceed the client's default 5s timeout — give this
      // specific request much more headroom.
      const res = await apiClient.post(
        '/integrations/canonical-properties/suggest-links',
        null,
        { timeout: 120000 }
      );
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      const timedOut = err.code === 'ECONNABORTED';
      setError(timedOut
        ? 'Suggestion analysis is taking longer than expected. Try again shortly.'
        : (err.response?.data?.detail || err.message || 'Failed to fetch suggestions'));
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const applyApprovedSuggestions = useCallback(async (approved) => {
    const payload = approved.map(s => ({
      canonical_key: s.canonical_key,
      integration_id: s.integration_id,
      link: {
        type_id: s.type_id,
        ext_key: s.ext_key,
        merge_strategy: 'latest',
        priority: 1,
      },
    }));
    const res = await apiClient.post(
      '/integrations/canonical-properties/apply-suggestions',
      { suggestions: payload },
    );
    await load();
    return res.data.applied;
  }, [load]);

  return {
    definitions,
    loading,
    error,
    suggestions,
    suggestionsLoading,
    load,
    createProperty,
    updateProperty,
    deleteProperty,
    upsertLink,
    removeLink,
    fetchSuggestions,
    applyApprovedSuggestions,
  };
}
